import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { API, useAuth } from '@/App';

const SaveButton = ({ itemType, itemId, className = '', variant = 'outline', size = 'default', showText = true }) => {
  const { user, token } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && token && itemId) {
      checkIfSaved();
    }
  }, [user, token, itemId]);

  const checkIfSaved = async () => {
    try {
      const response = await axios.get(
        `${API}/saved-items/check/${itemType}/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSaved(response.data.is_saved);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please sign in to save items');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isSaved ? '/saved-items/unsave' : '/saved-items/save';
      await axios.post(
        `${API}${endpoint}`,
        { item_type: itemType, item_id: itemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsSaved(!isSaved);
      toast.success(isSaved ? 'Removed from saved' : 'Saved successfully');
    } catch (error) {
      toast.error('Failed to update saved status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={isSaved ? 'default' : variant}
      size={size}
      onClick={toggleSave}
      disabled={loading}
      className={`${className} ${isSaved ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}`}
      data-testid={`save-${itemType}-${itemId}`}
    >
      <Heart 
        className={`h-4 w-4 ${showText ? 'mr-2' : ''} ${isSaved ? 'fill-current' : ''}`} 
      />
      {showText && (isSaved ? 'Saved' : 'Save')}
    </Button>
  );
};

export default SaveButton;
